/**
 * Frontend-only helpers for Desktop Location Fallback (address search + map pin).
 * Does not change composeFullAddress, payload shape, or backend APIs.
 */

const NOMINATIM_UA = 'SparePE-PartnerSignup/1.0';
const INDIA_VIEWBOX = '68.1,37.1,97.4,6.5'; // left,top,right,bottom for Nominatim

function pickCity(addr = {}) {
  return (
    addr.city ||
    addr.town ||
    addr.village ||
    addr.suburb ||
    addr.municipality ||
    addr.county ||
    addr.state_district ||
    ''
  );
}

function pickLandmark(addr = {}) {
  return (
    addr.neighbourhood ||
    addr.suburb ||
    addr.quarter ||
    addr.hamlet ||
    addr.locality ||
    ''
  );
}

function normalizeSuggestion(raw) {
  const lat = Number(raw.lat);
  const lng = Number(raw.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return {
    id: raw.id || `${lat},${lng},${raw.label}`,
    label: String(raw.label || '').trim(),
    lat,
    lng,
    houseNo: String(raw.houseNo || '').trim(),
    street: String(raw.street || '').trim(),
    landmark: String(raw.landmark || '').trim(),
    city: String(raw.city || '').trim(),
    state: String(raw.state || '').trim(),
    pinCode: String(raw.pinCode || '').replace(/\D/g, '').slice(0, 6),
  };
}

function fieldsFromNominatimAddress(addr = {}, displayName = '', lat, lng) {
  const houseNo = String(addr.house_number || '').trim();
  const street = String(addr.road || addr.pedestrian || addr.residential || '').trim();
  const landmark = pickLandmark(addr);
  const city = pickCity(addr);
  const state = String(addr.state || '').trim();
  const pinCode = String(addr.postcode || '').replace(/\D/g, '').slice(0, 6);
  const label = String(displayName || [houseNo, street, landmark, city, state, pinCode].filter(Boolean).join(', ')).trim();
  return normalizeSuggestion({
    id: `nom-${lat},${lng}`,
    label,
    lat,
    lng,
    houseNo,
    street,
    landmark,
    city,
    state,
    pinCode,
  });
}

/**
 * Address autocomplete suggestions (India-focused).
 * Photon first for speed; Nominatim as secondary source.
 */
export async function searchAddressSuggestions(query, { signal, limit = 6 } = {}) {
  const q = String(query || '').trim();
  if (q.length < 3) return [];

  const results = [];
  const seen = new Set();

  const push = (item) => {
    const normalized = normalizeSuggestion(item);
    if (!normalized) return;
    const key = `${normalized.lat.toFixed(5)},${normalized.lng.toFixed(5)},${normalized.label.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    results.push(normalized);
  };

  try {
    const photonRes = await fetch(
      `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=${limit}&lang=en&bbox=68.1,6.5,97.4,35.7`,
      { signal, headers: { Accept: 'application/json' } }
    );
    if (photonRes.ok) {
      const data = await photonRes.json();
      (data?.features || []).forEach((feature) => {
        const props = feature?.properties || {};
        const coords = feature?.geometry?.coordinates;
        const lng = Number(coords?.[0]);
        const lat = Number(coords?.[1]);
        const parts = [
          props.name,
          props.housenumber,
          props.street,
          props.locality || props.district,
          props.city,
          props.state,
          props.postcode,
        ].filter(Boolean);
        push({
          id: `ph-${props.osm_id || `${lat},${lng}`}`,
          label: parts.join(', ') || props.name,
          lat,
          lng,
          houseNo: props.housenumber || '',
          street: props.street || props.name || '',
          landmark: props.locality || props.district || '',
          city: props.city || '',
          state: props.state || '',
          pinCode: props.postcode || '',
        });
      });
    }
  } catch {
    // try nominatim below
  }

  if (results.length < limit) {
    try {
      const nominatimRes = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(`${q}, India`)}&format=json&addressdetails=1&limit=${limit}&countrycodes=in&viewbox=${INDIA_VIEWBOX}&bounded=0`,
        {
          signal,
          headers: { Accept: 'application/json', 'User-Agent': NOMINATIM_UA },
        }
      );
      if (nominatimRes.ok) {
        const data = await nominatimRes.json();
        (Array.isArray(data) ? data : []).forEach((hit) => {
          const lat = Number(hit.lat);
          const lng = Number(hit.lon);
          const fields = fieldsFromNominatimAddress(hit.address || {}, hit.display_name || '', lat, lng);
          if (fields) push(fields);
        });
      }
    } catch {
      // ignore
    }
  }

  return results.slice(0, limit);
}

/**
 * Reverse-geocode a map pin into existing signup address fields.
 */
export async function resolveFormAddressFromCoords(lat, lng) {
  const nLat = Number(lat);
  const nLng = Number(lng);
  if (!Number.isFinite(nLat) || !Number.isFinite(nLng)) return null;

  let nominatim = { address: {}, displayName: '' };
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${encodeURIComponent(nLat)}&lon=${encodeURIComponent(nLng)}&zoom=19&format=jsonv2&addressdetails=1`,
      { headers: { Accept: 'application/json', 'User-Agent': NOMINATIM_UA } }
    );
    if (response.ok) {
      const data = await response.json();
      nominatim = { address: data?.address || {}, displayName: data?.display_name || '' };
    }
  } catch {
    // continue with photon
  }

  let photon = {};
  try {
    const response = await fetch(
      `https://photon.komoot.io/reverse?lat=${encodeURIComponent(nLat)}&lon=${encodeURIComponent(nLng)}`
    );
    if (response.ok) {
      const data = await response.json();
      const props = data?.features?.[0]?.properties || {};
      photon = {
        houseNo: props.housenumber || '',
        street: props.street || '',
        landmark: props.locality || props.district || '',
        city: props.city || '',
        state: props.state || '',
        pinCode: props.postcode || '',
        label: [props.name, props.street, props.city, props.state, props.postcode].filter(Boolean).join(', '),
      };
    }
  } catch {
    // ignore
  }

  const fromNom = fieldsFromNominatimAddress(nominatim.address, nominatim.displayName, nLat, nLng);
  if (!fromNom) return null;

  return {
    ...fromNom,
    houseNo: fromNom.houseNo || photon.houseNo || '',
    street: fromNom.street || photon.street || '',
    landmark: fromNom.landmark || photon.landmark || '',
    city: fromNom.city || photon.city || '',
    state: fromNom.state || photon.state || '',
    pinCode: fromNom.pinCode || String(photon.pinCode || '').replace(/\D/g, '').slice(0, 6),
    label: fromNom.label || photon.label || `${nLat.toFixed(5)}, ${nLng.toFixed(5)}`,
  };
}

/** Build form patch from a selected/confirmed location (same field names as existing form). */
export function buildAddressFormPatchFromResolved(resolved) {
  if (!resolved) return null;
  const pinCode = String(resolved.pinCode || '').replace(/\D/g, '').slice(0, 6);
  const houseNo = String(resolved.houseNo || '').trim();
  const street = String(resolved.street || '').trim();
  const landmark = String(resolved.landmark || '').trim();
  const city = String(resolved.city || '').trim();
  const state = String(resolved.state || '').trim();
  const lat = Number(resolved.lat);
  const lng = Number(resolved.lng);
  const label = String(resolved.label || '').trim();

  return {
    houseNo,
    street,
    landmark,
    city,
    state,
    pinCode,
    latitude: String(lat),
    longitude: String(lng),
    capturedLocation: label || [houseNo, street, landmark, city, state, pinCode].filter(Boolean).join(', '),
    locationPinCode: pinCode,
    locationResolvedBy: 'fallback',
  };
}
